// NOTE: This is part of the lower level of the New Computation Model, and
// should be kept isolated from non-trivial Glide dependencies.  In
// particular, it shouldn't have to know anything about Glide table/column
// types/schemas.

import { hasOwnProperty } from "collection-utils";
import { GlideDateTime } from "../../date-time/glide-date-time";
import { parseUserDateTimeZoneAgnostic, parseUserDateTimeZoneAgnosticSync } from "../../date-time/parse-user-date-time";
import { makeDateFromString } from "../../date-time/support";
import { logError } from "../debug-print";
import { formatNumber } from "../format-number";
import { assert, checkString, defined, isArray, panic, parseNumber } from "../Support";
import type { ColumnPath, KeyPath, MutableRelativePath, Path, RelativePath, RelativePathRest } from "./paths";
import { copyPath, isColumnPath, isIDPath, isIndexPath, isKeyPath } from "./paths";
import type { Thunk } from "./thunk";

export type BasePrimitiveValue = string | number | boolean;
export type PrimitiveValue = BasePrimitiveValue | GlideDateTime | undefined;

export class LoadingValue {
    // For some reason TS needs this, or the `isLoadingValue` type assertion
    // doesn't work.
    public readonly dummy = true;
}

const loadingValueSingleton = new LoadingValue();

export function makeLoadingValue(): LoadingValue {
    return loadingValueSingleton;
}

export interface ColumnValues {
    [column: string]: GroundValue | Thunk;
}

export interface LoadedColumnValues {
    [column: string]: LoadedGroundValue;
}

interface AdditionalRowProperties {
    readonly $rowID: string;
    // Rows that are not visible are kept in tables and have computed columns
    // added, are passed through by sorts, but they're ignored by filters,
    // aggregates, and relations.  We use this to keep rows like the default
    // user profile row with computed columns.
    readonly $isVisible: boolean;
}

export interface Row extends ColumnValues, AdditionalRowProperties {}

export interface LoadedRow extends LoadedColumnValues, AdditionalRowProperties {}

export type Table = Map<string, Row>;
export type GroundTable = readonly Row[];
export type ArrayValue = readonly PrimitiveValue[];

export type TopLevelValue = Row | Table;
export type LoadedGroundValue = PrimitiveValue | Row | Table | ArrayValue;
export type GroundValue = LoadedGroundValue | LoadingValue;

export function isLoadingValue<T>(v: T | LoadingValue): v is LoadingValue {
    return v instanceof LoadingValue;
}

export function loadedDefinedMap<T, U>(v: T | LoadingValue | undefined, f: (v: T) => U): U | LoadingValue | undefined {
    if (v === undefined) return undefined;
    if (isLoadingValue(v)) return v;
    return f(v);
}

export function nullLoadingToUndefined<T>(v: T | LoadingValue | null): T | undefined {
    if (v === null || isLoadingValue(v)) return undefined;
    return v;
}

export function isLoadedRow(r: Row): r is LoadedRow {
    return !Object.values(r).some(isLoadingValue);
}

export function getSymbolicRepresentationForGroundValue(v: GroundValue): string {
    if (v === undefined) {
        return "undefined";
    } else if (isLoadingValue(v)) {
        return "LOADING";
    } else {
        return JSON.stringify(v);
    }
}

function removeLastPathElement<T extends Path>(path: T): { path: T; last: RelativePath } | undefined {
    assert(path !== undefined);

    if (path.rest === undefined) return undefined;

    const copy = defined(copyPath(path));
    let p: Path | undefined = copy;
    while (defined(defined(p).rest).rest !== undefined) {
        p = defined(p).rest;
    }
    const l = defined(defined(p).rest);
    (p as MutableRelativePath).rest = undefined;

    return { path: copy as T, last: l };
}

export function isThunk(v: LoadedGroundValue | Thunk): v is Thunk {
    return typeof v === "function";
}

export function setThunkColumn(r: Row, p: string, f: () => GroundValue): void {
    r[p] = () => {
        r[p] = f();
    };
}

export function isArrayValue(v: LoadedGroundValue): v is ArrayValue {
    // `isArray` refuses here because of some type system weirdness.
    return Array.isArray(v);
}

export function asArrayValue(v: LoadedGroundValue): ArrayValue {
    assert(isArrayValue(v));
    return v;
}

export function getArrayItem(a: ArrayValue, index: number): PrimitiveValue {
    return a[index];
}

export function isTable(v: LoadedGroundValue): v is Table {
    return v instanceof Map;
}

export function asTable(v: LoadedGroundValue): Table {
    if (!isTable(v)) {
        logError("Not a table", JSON.stringify(v));
        return panic("Not a table");
    }
    return v;
}

export function isBasePrimitiveValue(v: unknown): v is BasePrimitiveValue {
    return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

export function asMaybeBasePrimitive(v: LoadedGroundValue): BasePrimitiveValue | undefined {
    if (isBasePrimitiveValue(v)) {
        return v;
    } else {
        return undefined;
    }
}

export function isPrimitiveValue(v: unknown): v is PrimitiveValue {
    return v === undefined || isBasePrimitiveValue(v) || v instanceof GlideDateTime;
}

export function isPrimitive(v: LoadedGroundValue): v is PrimitiveValue {
    return isPrimitiveValue(v);
}

export function asMaybePrimitive(v: LoadedGroundValue): PrimitiveValue {
    if (!isPrimitive(v)) return undefined;
    return v;
}

export function asPrimitive(v: LoadedGroundValue): PrimitiveValue {
    assert(isPrimitive(v));
    return v;
}

// This is mostly a copy of the eponymous function in `component-helpers.ts`
export function asMaybeString(v: LoadedGroundValue): string | undefined {
    if (!isPrimitive(v)) return undefined;

    if (typeof v === "string") return v;
    if (typeof v === "number") return formatNumber(v);
    if (typeof v === "boolean") return v.toString();
    if (v instanceof GlideDateTime) return v.asString();
    return undefined;
}

export function asString(v: LoadedGroundValue): string {
    return asMaybeString(v) ?? "";
}

// function asBoolean(v: LoadedGroundValue): boolean {
//     return v !== undefined && v !== false && v !== 0;
// }

export function asMaybeNumber(v: LoadedGroundValue): number | undefined {
    if (v === undefined) return undefined;
    if (typeof v === "number") return v;
    if (typeof v === "boolean") return v ? 1 : 0;
    if (typeof v === "string") {
        const n = parseNumber(v);
        if (n !== undefined) return n;
    }
    return undefined;
}

export function asMaybeBoolean(v: LoadedGroundValue): boolean | undefined {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    if (typeof v === "string") {
        if (["true", "yes", "y", "1", "x"].indexOf(v.toLowerCase()) >= 0) return true;
        if (["false", "no", "n", "0", "-"].indexOf(v.toLowerCase()) >= 0) return false;
    }
    return undefined;
}

export function asBoolean(v: LoadedGroundValue): boolean {
    return asMaybeBoolean(v) ?? false;
}

const datesForPrimitives = new Map<LoadedGroundValue, GlideDateTime | false>();

export function asMaybeDate(v: LoadedGroundValue): GlideDateTime | undefined {
    if (v instanceof GlideDateTime) return v;

    let d = datesForPrimitives.get(v);
    if (d === false) return undefined;
    if (d !== undefined) return d;

    if (typeof v === "number") {
        d = GlideDateTime.fromTimeZoneAgnosticValue(v);
    } else if (typeof v === "string") {
        const date = makeDateFromString(v, true);
        if (date !== undefined) {
            d = GlideDateTime.fromTimeZoneAwareDate(date);
        }
    }

    datesForPrimitives.set(v, d ?? false);

    return d;
}

export function parseValueAsGlideDateTimeSync(v: LoadedGroundValue): GlideDateTime | undefined {
    if (typeof v === "string") {
        return parseUserDateTimeZoneAgnosticSync(v, true);
    }
    return asMaybeDate(v);
}

export function parseValueAsGlideDateTime(
    v: LoadedGroundValue
): Promise<GlideDateTime | undefined> | GlideDateTime | undefined {
    if (typeof v === "string") {
        return parseUserDateTimeZoneAgnostic(v, true);
    }
    return asMaybeDate(v);
}

export function isRow(v: LoadedGroundValue): v is Row {
    if (isPrimitive(v) || isArray(v)) return false;
    return hasOwnProperty(v, "$rowID");
}

export function asRow(v: LoadedGroundValue): Row {
    if (!isRow(v)) {
        logError("Not a row", JSON.stringify(v));
        return panic("Not a row");
    }
    return v;
}

// export function getTableRow(t: Table, index: number): Row | undefined {
//     return definedMap(getArrayItem(t, index), asRow);
// }

export function getRowColumn(r: Row, c: string): GroundValue {
    let v = r[c];
    if (v === undefined) return undefined;
    if (isLoadingValue(v)) return v;
    if (isThunk(v)) {
        v();
        v = r[c];
        if (isLoadingValue(v)) return v;
        assert(!isThunk(v));
    }
    return v;
}

export function getRowID(r: Row): string {
    return checkString(getRowColumn(r, "$rowID"));
}

export function isTruthy(v: LoadedGroundValue): boolean {
    return v !== undefined && v !== false && (!isArrayValue(v) || v.length > 0);
}

export function isNotEmpty(v: LoadedGroundValue): boolean {
    if (v === undefined) return false;
    if (v === "") return false;
    if (typeof v === "number" && isNaN(v)) return false;
    if (isArray(v)) return v.length > 0;
    if (isTable(v)) return v.size > 0;
    return true;
}

export function arrayMap<T>(a: ArrayValue, f: (v: LoadedGroundValue) => T): T[] {
    const result: T[] = [];
    for (let i = 0; i < a.length; i++) {
        result.push(f(getArrayItem(a, i)));
    }
    return result;
}

export function arrayFilter(a: ArrayValue, f: (v: LoadedGroundValue) => boolean): LoadedGroundValue[] {
    const result: LoadedGroundValue[] = [];
    for (let i = 0; i < a.length; i++) {
        const item = getArrayItem(a, i);
        if (f(item)) {
            result.push(item);
        }
    }
    return result;
}

// function findRowIndexWithID(t: Table, id: string): number | undefined {
//     for (let i = 0; i < t.length; i++) {
//         const r = defined(getTableRow(t, i));
//         if (getRowID(r) === id) {
//             return i;
//         }
//     }
//     return undefined;
// }

// function findRowWithID(t: Table, id: string): Row | undefined {
//     const index = findRowIndexWithID(t, id);
//     if (index === undefined) return undefined;
//     return getTableRow(t, index);
// }

export function arrayForEach(a: ArrayValue, f: (v: LoadedGroundValue, i: number) => unknown): void {
    for (let i = 0; i < a.length; i++) {
        const v = defined(getArrayItem(a, i));
        f(v, i);
    }
}

// Returns the number of rows processed
export function tableForEach(t: Table, includeInvisible: boolean, f: (r: Row, i: number) => unknown): number {
    let i = 0;
    for (const r of t.values()) {
        if (!includeInvisible && !r.$isVisible) continue;

        f(r, i);
        i++;
    }
    return i;
}

export function tableMap<T>(t: Table, includeInvisible: boolean, f: (r: Row) => T): T[] {
    const result: T[] = [];
    tableForEach(t, includeInvisible, r => result.push(f(r)));
    return result;
}

export function containerAsArray(tableOrArray: LoadedGroundValue): readonly LoadedGroundValue[] | undefined {
    if (isTable(tableOrArray)) {
        return Array.from(tableOrArray.values());
    } else if (isArrayValue(tableOrArray)) {
        return tableOrArray;
    } else {
        return undefined;
    }
}

export function forEachItem<T>(
    tableOrArray: LoadedGroundValue,
    f: (i: LoadedGroundValue) => T | undefined
): T | undefined {
    if (isTable(tableOrArray)) {
        for (const r of tableOrArray.values()) {
            if (!r.$isVisible) continue;
            const v = f(r);
            if (v !== undefined) {
                return v;
            }
        }
        return undefined;
    } else if (isArrayValue(tableOrArray)) {
        for (const i of tableOrArray) {
            const v = f(i);
            if (v !== undefined) {
                return v;
            }
        }
        return undefined;
    } else {
        return panic("Can only iterate over arrays and tables");
    }
}

// function forceTable(t: Table): GroundTable {
//     for (let i = 0; i < t.length; i++) {
//         getTableRow(t, i);
//     }
//     return t as GroundTable;
// }

export function follow(gv: GroundValue, path: RelativePath): GroundValue {
    let p: RelativePathRest = path;
    while (p !== undefined) {
        if (gv === undefined) return undefined;
        if (isLoadingValue(gv)) return gv;
        if (isKeyPath(p)) {
            const r = asRow(gv);
            gv = getRowColumn(r, p.key);
        } else if (isIndexPath(p)) {
            const a = asArrayValue(gv);
            if (p.index >= 0) {
                gv = getArrayItem(a, p.index);
            } else {
                gv = getArrayItem(a, a.length + p.index);
            }
        } else if (isIDPath(p)) {
            const t = asTable(gv);
            gv = t.get(p.id);
        } else {
            const { column } = p;
            let hasLoading: LoadingValue | undefined;
            gv = tableMap(asTable(gv), false, r => {
                const v = getRowColumn(r, column);
                if (isLoadingValue(v)) {
                    hasLoading = v;
                    return undefined;
                }
                return asPrimitive(v);
            });
            if (hasLoading !== undefined) return hasLoading;
        }
        p = p.rest;
    }
    return gv;
}

export function deconstructTableColumnPath<T extends Path>(
    path: T
): { tablePath: T; columnPath: ColumnPath; keyPath: KeyPath } {
    const { path: tablePath, last: columnPath } = defined(removeLastPathElement(path));
    assert(isColumnPath(columnPath) && columnPath.rest === undefined);
    return {
        tablePath,
        columnPath,
        keyPath: {
            key: columnPath.column,
            rest: columnPath.rest,
        },
    };
}
